import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Shell } from '../componentes/Shell.jsx';
import { guardarConConexion } from '../nucleo/api/cliente.js';
import { actualizarCategoria, consultarCategorias, crearCategoria } from '../nucleo/api/nucleoApi.js';
import AvisoDeError from '../nucleo/avisos/AvisoDeError.jsx';

// Nombres de icono admitidos. Los cinco ultimos entran con la siembra 2.0
// (D-13): Gasolina, Mascotas, Aguinaldo, Venta e Intereses. Si un icono
// sembrado no estuviera en esta lista, al abrir la categoria el selector
// quedaria vacio y la primera edicion le cambiaria el icono sin querer.
const ICONOS = ['cart', 'restaurant', 'car', 'fuel', 'home', 'bolt', 'health', 'school', 'movie', 'shirt', 'pet', 'shield', 'wallet', 'star', 'briefcase', 'tag', 'percent', 'gift', 'refresh', 'dots'];

/**
 * Categoría — alta y edición (RN-006, RN-007, RN-024, RN-045g).
 *
 * El color es UNO SOLO y no se reescribe al cambiar de tema: la vista previa lo
 * muestra sobre los dos fondos para comprobar que se distingue en ambos (RNF-023).
 *
 * ---------------------------------------------------------------------------
 * INCREMENTO 10 · H-08 · EL ALTA YA NO PIDE EL ORDEN (RF-159, D-265, RN-463).
 *
 * Aquí estaba la fábrica de órdenes duplicados: el formulario arrancaba con
 * `orden: 1` y lo enviaba tal cual, así que toda categoría nueva nacía EMPATADA
 * con la primera categoría sembrada de su tipo si la persona no tocaba ese
 * campo —y no tenía por qué tocarlo—. Ahora el orden de una categoría nueva lo
 * asigna el servidor: el mayor de su tipo más uno, contando también las
 * inactivas (RN-464).
 *
 * EN LA EDICIÓN EL CAMPO SE QUEDA INTACTO, y la diferencia es intencional, no un
 * olvido: editar es hoy la única forma de llevar una categoría a una posición
 * concreta sin dar N toques en las flechas.
 * ---------------------------------------------------------------------------
 */
export default function CategoriaEditar() {
  const { idCategoria } = useParams();
  const navegar = useNavigate();
  const esNueva = !idCategoria;

  // En el alta `orden` queda sin valor a propósito: lo asigna el servidor
  // (RF-159). En la edición lo rellena la consulta con el valor guardado.
  const [datos, setDatos] = useState({ tipo: 'GASTO', nombre: '', color: '#E64A19', icono: 'cart', orden: '', version: undefined });
  const [error, setError] = useState(null);
  const [esSistema, setEsSistema] = useState(false);

  useEffect(() => {
    if (esNueva) return;
    consultarCategorias({ soloActivas: false })
      .then((lista) => {
        const encontrada = lista.find((c) => String(c.idCategoria) === String(idCategoria));
        if (!encontrada) { setError({ mensaje: 'La categoría seleccionada ya no existe.' }); return; }
        setDatos({
          tipo: encontrada.tipo,
          nombre: encontrada.nombre,
          color: encontrada.color ?? '#546E7A',
          icono: encontrada.icono ?? 'dots',
          orden: encontrada.orden,
          version: encontrada.version,
        });
        setEsSistema(encontrada.esSistema);
      })
      .catch(setError);
  }, [idCategoria, esNueva]);

  async function guardar() {
    setError(null);
    if (!datos.nombre.trim()) return setError({ mensaje: 'Escribe el nombre de la categoría.' });

    try {
      const peticion = {
        tipo: datos.tipo,
        nombre: datos.nombre.trim(),
        color: datos.color,
        icono: datos.icono,
        // En el alta no se manda orden: lo decide el servidor (RF-159, §7 de la
        // nota de contrato). El campo sigue ACEPTÁNDOSE en la petición —no se
        // responde CAMPO_NO_ADMITIDO, para no romper a ningún consumidor— y sigue
        // viajando en la salida con el valor que el servidor asignó.
        ...(esNueva ? {} : { orden: Number(datos.orden) }),
        version: datos.version,
      };

      await guardarConConexion(() => (esNueva
        ? crearCategoria(peticion)
        : actualizarCategoria(idCategoria, peticion)));

      // RF-148, RN-391: REEMPLAZA el paso del formulario, no lo deja debajo.
      return navegar('/ajustes/categorias', { replace: true });
    } catch (fallo) {
      return setError(fallo);
    }
  }

  return (
    <Shell titulo={esNueva ? 'Nueva categoría' : 'Editar categoría'} volverA="/ajustes/categorias">
      <AvisoDeError error={error} />

      <div className="card">
        <label htmlFor="nombre">Nombre</label>
        <input id="nombre" value={datos.nombre} onChange={(e) => setDatos({ ...datos, nombre: e.target.value })} placeholder="Por ejemplo, Supermercado" />
        <div className="ayuda">Único dentro de su tipo, sin distinguir mayúsculas ni acentos.</div>

        <label htmlFor="tipo">Tipo</label>
        <select id="tipo" value={datos.tipo} onChange={(e) => setDatos({ ...datos, tipo: e.target.value })}>
          <option value="GASTO">Gasto</option>
          <option value="INGRESO">Ingreso</option>
        </select>
        <div className="ayuda">
          El tipo no se puede cambiar si la categoría ya tiene movimientos o presupuesto de
          cualquier persona: en ese caso se desactiva y se crea una nueva.
        </div>

        <label htmlFor="color">Color</label>
        <input id="color" type="color" value={datos.color} onChange={(e) => setDatos({ ...datos, color: e.target.value })} style={{ padding: 4 }} />

        <label htmlFor="icono">Ícono</label>
        <select id="icono" value={datos.icono} onChange={(e) => setDatos({ ...datos, icono: e.target.value })}>
          {ICONOS.map((icono) => <option key={icono} value={icono}>{icono}</option>)}
        </select>

        {/* RF-159, D-265 · El campo SOLO existe en la edición. En el alta no queda
            nada en su lugar: ni otro campo, ni una casilla, ni un número a la
            vista. */}
        {!esNueva && (
          <>
            <label htmlFor="orden">Orden de presentación</label>
            <input id="orden" type="number" min="1" value={datos.orden} onChange={(e) => setDatos({ ...datos, orden: e.target.value })} />
            <div className="ayuda">
              El lugar que ocupa en la lista de su tipo. Cambiarlo aquí la mueve de sitio.
              Es compartido: al reordenar, ambas personas ven el nuevo orden.
            </div>
          </>
        )}
      </div>

      {esNueva && (
        <div className="aviso i">
          <b style={{ display: 'block', marginBottom: 3 }}>¿Y dónde va a quedar la categoría nueva?</b>
          {/* Fila 18 del inventario del incremento 15 (RF-222): salió la justificación del
              diseño («que es donde uno espera encontrar lo que acaba de crear»). */}
          Al final de la lista de su tipo. Desde ahí la mueves con las flechas del renglón, o le
          pones el número que quieras al editarla.
        </div>
      )}

      {/* Vista previa del color en los dos temas (RNF-023, RN-045g). */}
      <div className="card">
        <h3>Cómo se verá en los dos temas</h3>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1, background: '#ffffff', border: '1px solid #dbe1ea', borderRadius: 10, padding: 10 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ width: 22, height: 22, borderRadius: 6, background: datos.color, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.25)' }} />
              <span style={{ color: '#1b2431', fontSize: 13 }}>{datos.nombre || 'Categoría'}</span>
            </div>
            <div style={{ color: '#55606f', fontSize: 11, marginTop: 6 }}>Tema claro</div>
          </div>

          <div style={{ flex: 1, background: '#18202c', border: '1px solid #2d3a4b', borderRadius: 10, padding: 10 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ width: 22, height: 22, borderRadius: 6, background: datos.color, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.35)' }} />
              <span style={{ color: '#e7edf6', fontSize: 13 }}>{datos.nombre || 'Categoría'}</span>
            </div>
            <div style={{ color: '#a6b2c2', fontSize: 11, marginTop: 6 }}>Tema oscuro</div>
          </div>
        </div>

        <div className="ayuda">
          El color guardado es uno solo y <b>no se reescribe al cambiar de tema</b>: se usa como
          indicador y el texto toma su color del token del tema.
        </div>
      </div>

      {esSistema && (
        <div className="aviso i">
          Es una categoría del catálogo inicial. Se puede renombrar y desactivar, pero no eliminar.
        </div>
      )}

      <div style={{ margin: '10px 12px' }}>
        <button className="btn ancho" onClick={guardar}>Guardar</button>
      </div>

      <div className="info">
        Cualquiera de las dos personas puede crear y editar cualquier categoría, incluidas las que
        creó la otra. Si ambas editan a la vez, gana la primera escritura y la segunda
        recibe un aviso para recargar el dato vigente.
      </div>
    </Shell>
  );
}
